/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { task } from 'ember-concurrency';
import { UserFlags } from 'cio/models/user';
import AuthService from 'cio/services/auth';

import EventBusService, {
  INSERT_LIQUID,
  LIQUID_ENABLED_FIELD_FOCUS_IN,
  LIQUID_ENABLED_FIELD_FOCUS_OUT,
} from 'cio/services/event-bus';
import AnalyticsService from 'cio/services/analytics';
import { DataActionComposerSectionType } from 'cio/utils/campaigns/data-action';
import CampaignModel from 'cio/models/campaign';
import { ComposerActionTypeName } from 'cio/utils/composer';
import AlertsService from 'cio/services/alerts';

interface LiquidFallbackState {
  liquid?: string;
  focusedLiquidField: string | null;
  focusedLiquidFieldIndex: number | null;
}

interface InsertLiquidComponentArgs {
  onLiquidFocusIn?: (field: [string | null, number | null] | string | null) => unknown;
  onLiquidFocusOut?: () => unknown;
  analyticsData?: {
    campaign?: CampaignModel;
    actionTypeName?: ComposerActionTypeName;
    selectedTab?: string;
  };
}

export default class InsertLiquidComponent extends Component<InsertLiquidComponentArgs> {
  @service eventBus!: EventBusService;
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;

  @tracked isShowingLiquidFallbackModal = false;
  @tracked fallbackState: LiquidFallbackState = { liquid: '', focusedLiquidField: null, focusedLiquidFieldIndex: null };
  @tracked isLiquidFieldFocused = false;
  @tracked focusedLiquidField: string | null = null;
  @tracked focusedLiquidFieldIndex: number | null = null;

  get showLiquidFallbackModal() {
    return this.auth.user?.userFlags.show_liquid_fallback_modal ?? true;
  }

  constructor(owner: unknown, args: InsertLiquidComponentArgs) {
    super(owner, args);
    this.subscribeToLiquidFocus();
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribeFromLiquidFocus();
  }

  @action
  subscribeToLiquidFocus() {
    this.eventBus.subscribe(LIQUID_ENABLED_FIELD_FOCUS_IN, this, this.liquidFocusIn);
    this.eventBus.subscribe(LIQUID_ENABLED_FIELD_FOCUS_OUT, this, this.liquidFocusOut);
  }

  @action
  unsubscribeFromLiquidFocus() {
    this.eventBus.unsubscribe(LIQUID_ENABLED_FIELD_FOCUS_IN, this, this.liquidFocusIn);
    this.eventBus.unsubscribe(LIQUID_ENABLED_FIELD_FOCUS_OUT, this, this.liquidFocusOut);
  }

  updateShowFallbackModalFlag = task(async (value: boolean) => {
    if (this.auth.user == null) {
      this.alerts.error(
        'insert-liquid.update-show-fallback-modal-flag.user-missing',
        'Unable to find current user. Refresh the page and try again.',
      );
      return;
    }

    let key: keyof UserFlags = 'show_liquid_fallback_modal';
    await this.auth.user.updateUserFlag(key, value);
  });

  @action
  insertLiquid(event: Event | null, liquid: string) {
    event?.preventDefault();
    event?.stopImmediatePropagation();
    this.eventBus.publish(INSERT_LIQUID, [this.focusedLiquidField, this.focusedLiquidFieldIndex], liquid);
  }

  @action
  insertLiquidWithFallback(fallback: string) {
    let { focusedLiquidField, focusedLiquidFieldIndex, liquid } = this.fallbackState;
    this.eventBus.publish(INSERT_LIQUID, [focusedLiquidField, focusedLiquidFieldIndex], liquid, fallback);
    this.trackAddDataCompleted(!isEmpty(fallback), fallback, liquid);
  }

  @action
  openLiquidFallbackModal(event: Event | null, liquid: string) {
    let { focusedLiquidField } = this;

    // if no input field is focussed, it's probably a new row
    // so fallback modal doesn't make sense
    let shouldSkipFallbackModal = isEmpty(focusedLiquidField) || !this.showLiquidFallbackModal;

    if (
      focusedLiquidField != null &&
      (
        [
          DataActionComposerSectionType.Recipient,
          DataActionComposerSectionType.EventName,
          DataActionComposerSectionType.BodyRow,
        ] as string[]
      ).includes(focusedLiquidField)
    ) {
      shouldSkipFallbackModal = true;
    }

    if (shouldSkipFallbackModal) {
      this.insertLiquid(event, liquid);
      return;
    }

    event?.preventDefault();
    event?.stopImmediatePropagation();

    this.isShowingLiquidFallbackModal = true;
    // capture state of the focusedField, & liquid to be added when Add was clicked
    this.fallbackState = {
      liquid,
      focusedLiquidField,
      focusedLiquidFieldIndex: this.focusedLiquidFieldIndex,
    };

    this.trackAddDataStarted(liquid ?? '');
  }

  @action
  closeLiquidFallbackModal(isDontShowAgainChecked: boolean) {
    if (isDontShowAgainChecked) {
      this.updateShowFallbackModalFlag.perform(false);
    }
    this.isShowingLiquidFallbackModal = false;
  }

  private liquidFocusIn(field: [string | null, number | null] | string | null) {
    if (Array.isArray(field)) {
      let [fieldName, fieldIndex] = field;
      this.focusedLiquidField = fieldName;
      this.focusedLiquidFieldIndex = fieldIndex;
    } else {
      this.focusedLiquidField = field;
      this.focusedLiquidFieldIndex = null;
    }
    this.isLiquidFieldFocused = true;
    this.args.onLiquidFocusIn?.(field);
  }

  private liquidFocusOut() {
    this.focusedLiquidField = '';
    this.isLiquidFieldFocused = false;
    this.args.onLiquidFocusOut?.();
  }

  private trackAddDataStarted(liquid: string) {
    this.analytics.track('message', 'add_data_started', {
      ...(this.args.analyticsData ?? {}),
      liquid,
    });
  }

  private trackAddDataCompleted(isFallbackSet: boolean, fallbackValue: string, liquid?: string) {
    this.analytics.track('message', 'add_data_completed', {
      ...(this.args.analyticsData ?? {}),
      liquid,
      isFallbackSet,
      fallbackValue,
    });
  }
}
